/**
 * Elle fiyat girişi. Aşama 1'de tüm fiyatlar elle; Aşama 2'de merkez devralır
 * ve bu ekran yalnızca merkezde bulunmayan araçlar için kalır.
 */

import { ElleDegerlemeYolu } from "../kabiliyet/IceAktarYolu";
import { useCallback, useState } from "react";
import type { ManualPrice } from "@parafon/contracts/local-api";
import { isKnownCurrency } from "@parafon/contracts";
import { M } from "../dil";
import { formatDecimal } from "../lib/decimal";
import { formatIsoDate, todayIso } from "../lib/format";
import { useCentral } from "../central/CentralContext";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync, useMutation } from "../repo/useAsync";
import { DecimalInput, readDecimalField } from "../ui/DecimalInput";
import { CurrencySelect } from "../ui/CurrencySelect";
import {
  Button,
  DateInput,
  EmptyState,
  ErrorBlock,
  Field,
  InlineError,
  LoadingBlock,
  Notice,
  NumberCell,
  Screen,
  TextInput,
} from "../ui/primitives";

export function ManualPricesScreen() {
  const { repo, revision, invalidate } = useRepositoryContext();
  const [ref, setRef] = useState("");
  const [lookupRef, setLookupRef] = useState("");

  const load = useCallback(
    async (): Promise<readonly ManualPrice[]> => (lookupRef === "" ? [] : repo.manualPrices.listFor(lookupRef)),
    [repo, lookupRef],
  );
  const { state, reload } = useAsync(load, [repo, lookupRef, revision]);

  return (
    <Screen
      title={M.izleme.elleFiyat.baslik}
      description={M.izleme.elleFiyat.aciklama}
      toolbar={<ElleDegerlemeYolu hedef="degerleme" />}
    >
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <div className="space-y-3">
          <PriceForm
            onSaved={(savedRef) => {
              setLookupRef(savedRef);
              setRef(savedRef);
              invalidate();
            }}
          />
          <Notice tone="info" title={M.izleme.elleFiyat.asama2Baslik}>
            <p>
              {M.izleme.elleFiyat.asama2Once}
              <code>unresolved</code>
              {M.izleme.elleFiyat.asama2Sonra}
            </p>
          </Notice>
        </div>

        <div className="pf-card min-w-0" data-kart-bolum="liste">
          <form
            className="flex flex-wrap items-end gap-2 border-b border-line p-3"
            onSubmit={(e) => {
              e.preventDefault();
              setLookupRef(ref.trim());
            }}
          >
            <Field label={M.izleme.elleFiyat.kimlikleListele} htmlFor="mp-lookup" className="min-w-52 flex-1">
              <TextInput
                id="mp-lookup"
                value={ref}
                placeholder="BIST:THYAO"
                onChange={(e) => setRef(e.currentTarget.value)}
              />
            </Field>
            <Button type="submit" variant="primary">
              {M.izleme.elleFiyat.listele}
            </Button>
          </form>

          {lookupRef === "" ? (
            <EmptyState title={M.izleme.elleFiyat.kimlikGirin} description={M.izleme.elleFiyat.kimlikGirinMetin} />
          ) : state.status === "loading" && state.data === undefined ? (
            <LoadingBlock />
          ) : state.status === "error" ? (
            <ErrorBlock error={state.error} onRetry={reload} />
          ) : (state.data ?? []).length === 0 ? (
            <EmptyState title={M.izleme.elleFiyat.kayitYok} />
          ) : (
            <div className="overflow-x-auto">
              <table className="pf-table">
                <caption className="sr-only">{M.izleme.elleFiyat.tabloBasligi(lookupRef)}</caption>
                <thead>
                  <tr>
                    <th scope="col" className="pf-num">
                      {M.izleme.elleFiyat.tarih}
                    </th>
                    <th scope="col" className="pf-num">
                      {M.izleme.elleFiyat.kapanis}
                    </th>
                    <th scope="col" className="pf-num">
                      {M.izleme.elleFiyat.paraBirimi}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(state.data ?? []).map((p) => (
                    <tr key={`${p.instrumentRef}:${p.date}`}>
                      <td className="pf-num">{formatIsoDate(p.date)}</td>
                      <td className="pf-num">
                        <NumberCell>{formatDecimal(p.close, { maxFractionDigits: 8 })}</NumberCell>
                      </td>
                      <td className="pf-num text-muted">{p.currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Screen>
  );
}

/**
 * İZLENEN ARAÇ İÇİN ELLE FİYAT KAYDEDİLEMEZ.
 *
 * Bir TEFAS fonunun günlük fiyatı TEKTİR ve kaynaktan gelir; elle girilen bir
 * sayının onun yerine geçmesi raporu kaynağın söylediğinden koparır. Motor
 * bunu zaten yok sayıyor (`valuation.ts` `trackedUnitPrice`) — form da kabul
 * ETMEMELİ, yoksa kullanıcı sayıyı girer, kaydeder ve raporda hiç göremez.
 *
 * Önbellek yoksa (uçak modu) kontrol yapılamaz; o durumda kayıt engellenmiyor
 * ama motor yine yok sayacağı için raporda damgası çıkıyor. "Bilmiyorum" ile
 * "izlenmiyor"u aynı saymak, izlenen bir araca sessizce izin vermek olurdu —
 * o yüzden form bunu da SÖYLÜYOR.
 */
function PriceForm({ onSaved }: { readonly onSaved: (ref: string) => void }) {
  const { repo } = useRepositoryContext();
  const { prices } = useCentral();
  const save = useMutation();
  const [instrumentRef, setInstrumentRef] = useState("");
  const [date, setDate] = useState(todayIso());
  const [close, setClose] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async (): Promise<void> => {
    const next: Record<string, string> = {};
    if (instrumentRef.trim() === "") next["ref"] = M.izleme.elleFiyat.kimlikZorunlu;
    if (date === "") next["date"] = M.izleme.elleFiyat.tarihZorunlu;
    if (!isKnownCurrency(currency)) next["currency"] = M.izleme.elleFiyat.paraBirimiSecin;
    const closeRead = readDecimalField(close, { required: true });
    if (!closeRead.ok) next["close"] = closeRead.message;

    const ref = instrumentRef.trim();
    if (ref !== "" && prices !== null) {
      const durum = await prices.instrumentState(ref);
      // `unresolved` araç izlemede AMA hiçbir kaynak çözemiyor: elle değerleme
      // onun için tek yol, engellenmemeli.
      if (durum !== null && durum.centralState !== "unresolved") {
        next["ref"] = M.izleme.elleFiyat.izlenenArac;
      }
    }

    setErrors(next);
    if (Object.keys(next).length > 0 || !closeRead.ok || closeRead.value === undefined) return;

    const ok = await save.run(async () => {
      // upsert artık yazılan kaydı döndürüyor (sözleşme değişikliği);
      // burada dönüş değerine ihtiyaç yok, void'e indiriyoruz.
      await repo.manualPrices.upsert({
        instrumentRef: instrumentRef.trim(),
        date,
        close: closeRead.value as string,
        currency: currency.trim().toUpperCase(),
      });
    });
    if (ok) {
      setClose("");
      onSaved(instrumentRef.trim());
    }
  };

  const err = (k: string): { error?: string } => {
    const v = errors[k];
    return v === undefined ? {} : { error: v };
  };

  return (
    <form
      className="pf-card space-y-3 p-3" data-kart-bolum="form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      noValidate
      aria-label={M.izleme.elleFiyat.formEtiketi}
    >
      <p className="text-[13px] font-semibold text-ink">{M.izleme.elleFiyat.fiyatKaydet}</p>

      <Field label={M.izleme.elleFiyat.aracKimligi} htmlFor="mp-ref" required {...err("ref")}>
        <TextInput
          id="mp-ref"
          value={instrumentRef}
          placeholder="BIST:THYAO"
          aria-invalid={errors["ref"] !== undefined}
          onChange={(e) => setInstrumentRef(e.currentTarget.value)}
        />
      </Field>

      <Field label={M.izleme.elleFiyat.tarih} htmlFor="mp-date" required {...err("date")}>
        <DateInput id="mp-date" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
      </Field>

      <DecimalInput label={M.izleme.elleFiyat.kapanis} value={close} onChange={setClose} required currency={currency} {...err("close")} />

      <Field label={M.izleme.elleFiyat.paraBirimi} htmlFor="mp-cur" required {...err("currency")}>
        <CurrencySelect
          id="mp-cur"
          className="pf-fig"
          value={currency}
          invalid={errors["currency"] !== undefined}
          onChange={setCurrency}
        />
      </Field>

      <InlineError error={save.error} />

      <Button type="submit" variant="primary" disabled={save.pending} className="w-full">
        {save.pending ? M.ortak.kaydediliyor : M.ortak.kaydet}
      </Button>
      <p className="text-[12px] text-faint">{M.izleme.elleFiyat.tekrarKayit}</p>
    </form>
  );
}
